import React from 'react'
import styled from '@emotion/styled'

import {Icon} from '@vio/atlas/Icon'
import {Brand as BookingPaymentBrand} from '@vio/core/src/api-types/bovio/response/booking'
import {Brand as PaymentMethodsBrand} from '@vio/core/src/api-types/bovio/response/payment_methods_settings'

type PaymentBrands = BookingPaymentBrand | PaymentMethodsBrand

// All valid SVG brand filenames
const SVG_BRANDS = [
  'dc',
  'discover',
  'amex',
  'jcb',
  'maestro',
  'mastercard',
  'union',
  'visa',
  'cb'
] as const

export type CreditCardBrand = (typeof SVG_BRANDS)[number]

const VALID_SVG_BRANDS = new Set<string>(SVG_BRANDS)

interface Props {
  brand: PaymentBrands | CreditCardBrand | string
  name: string
}

const StyledImage = styled.img`
  vertical-align: middle;
  width: 26px;
  height: 16px;
`

// Map BoVio API Brand enums to SVG filenames
const BRAND_TO_SVG: Partial<Record<PaymentBrands, CreditCardBrand>> = {
  // BookingBrand (snake_case)
  [BookingPaymentBrand.AmericanExpress]: 'amex',
  [BookingPaymentBrand.DinersClub]: 'dc',
  [BookingPaymentBrand.Discover]: 'discover',
  [BookingPaymentBrand.Jcb]: 'jcb',
  [BookingPaymentBrand.Maestro]: 'maestro',
  [BookingPaymentBrand.MasterCard]: 'mastercard',
  [BookingPaymentBrand.MasterCardDebit]: 'mastercard',
  [BookingPaymentBrand.UnionPay]: 'union',
  [BookingPaymentBrand.Visa]: 'visa',
  [BookingPaymentBrand.VisaDebit]: 'visa',
  [BookingPaymentBrand.VisaElectron]: 'visa',
  // PaymentMethodsBrand (Title Case)
  [PaymentMethodsBrand.AmericanExpress]: 'amex',
  [PaymentMethodsBrand.DinersClub]: 'dc',
  [PaymentMethodsBrand.Discover]: 'discover',
  [PaymentMethodsBrand.Jcb]: 'jcb',
  [PaymentMethodsBrand.Maestro]: 'maestro',
  [PaymentMethodsBrand.Mastercard]: 'mastercard',
  [PaymentMethodsBrand.MastercardDebit]: 'mastercard',
  [PaymentMethodsBrand.UnionPay]: 'union',
  [PaymentMethodsBrand.Visa]: 'visa',
  [PaymentMethodsBrand.VisaDebit]: 'visa',
  [PaymentMethodsBrand.VisaElectron]: 'visa'
}

const isPaymentBrand = (value: string): value is PaymentBrands => {
  return value in BRAND_TO_SVG
}

export const CreditCardLogo = ({brand, name}: Props) => {
  const mappedBrand = isPaymentBrand(brand) ? BRAND_TO_SVG[brand] : undefined
  const svgName = mappedBrand ?? brand

  if (!svgName || !VALID_SVG_BRANDS.has(svgName)) {
    return <Icon name="CreditCard" />
  }

  return (
    <StyledImage
      src={
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        require(
          `@vio/atlas/src/foundation/logos/paymentServiceLogo/creditCard/brand-${svgName}.svg`
        ).default.src
      }
      alt={name}
    />
  )
}
